import { useEffect, useRef } from 'react';

import { isNativePlatform } from '@/renderer/utils/isNativePlatform';

import { useRefUpdating } from './useRefUpdating';

/**
 * Reconcile-on-foreground primitive.
 *
 * Fires `onForeground` when the app/tab returns to the foreground, so a message
 * that arrived while the client was backgrounded — and was missed because the OS
 * throttled/suspended the WebSocket without a clean reconnect — is caught up the
 * moment the user comes back, instead of sitting unrendered until the next WS
 * event forces an update (#1659).
 *
 * Complements {@link useReconcileOnReconnect}: that fires on a WebSocket
 * false→true transition (a reconnect); this fires on wake even when the socket is
 * judged healthy and never reconnects — the case a backgrounded mobile client
 * hits most, since the OS can suspend the socket and resume it with a stale frame
 * without ever closing it.
 *
 * Triggers (either one fires the same catch-up):
 *  - Page Visibility (`visibilitychange` → visible): web and webview.
 *  - Capacitor `@capacitor/app` `appStateChange` → active: native, where a
 *    backgrounded WKWebView's `visibilitychange` is unreliable. Accessed via the
 *    global Capacitor bridge (same pattern as `useWebSocket2`) so it stays a pure
 *    no-op off-native and never statically imports `@capacitor/app` — a
 *    declared-but-not-installed import 500s guarded pages in this repo. The native
 *    shell registers the plugin; until then this is harmless and the Page
 *    Visibility path (plus the live-window poll) still covers catch-up.
 *
 * Deduped: a native resume commonly emits BOTH `visibilitychange` and
 * `appStateChange`, so a short window collapses them into ONE reconcile rather
 * than a storm. `onForeground` (typically `pollNow`) is itself idempotent — it
 * dedups fetched items by id — so an extra call would be safe anyway; the dedupe
 * just keeps it cheap.
 */
export function useReconcileOnForeground(
  onForeground: () => void,
  { dedupeMs = 2000 }: { dedupeMs?: number } = {},
) {
  const cbRef = useRefUpdating(onForeground);
  // -Infinity = "never fired", so the first wake always fires regardless of the
  // wall clock (mirrors useReconcileOnReconnect).
  const lastFiredRef = useRef(-Infinity);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const fire = () => {
      const now = Date.now();
      if (now - lastFiredRef.current < dedupeMs) {
        return;
      }
      lastFiredRef.current = now;
      cbRef.current?.();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fire();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    let cancelled = false;
    let handle: { remove?: () => void } | undefined;

    if (isNativePlatform()) {
      const app = (
        window as unknown as {
          Capacitor?: {
            Plugins?: {
              App?: {
                addListener?: (
                  event: string,
                  cb: (state: { isActive?: boolean }) => void,
                ) => { remove?: () => void } | Promise<{ remove?: () => void }>;
              };
            };
          };
        }
      ).Capacitor?.Plugins?.App;

      if (app?.addListener) {
        Promise.resolve(
          app.addListener('appStateChange', (state) => {
            if (state?.isActive) {
              fire();
            }
          }),
        )
          .then((h) => {
            if (cancelled) {
              h?.remove?.();
            } else {
              handle = h;
            }
          })
          .catch(() => {
            // App plugin not registered in this shell — the Page Visibility path
            // and the live-window poll still cover catch-up. Harmless.
          });
      }
    }

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      handle?.remove?.();
    };
  }, [dedupeMs]);
}
